const splitCodes = val =>
  val ? String(val).split(',').map(s => s.trim()).filter(Boolean) : [];

/**
 * Extract province/district/DSD codes from a DoSS unique ID.
 * ID format: DDSSSSSSSS  (DD=district 2 digits, SS=DSD 2 digits, rest=sequence)
 */
export function locationFromUniqueId(uniqueId) {
  if (!uniqueId || String(uniqueId).length < 4) return null;
  const id = String(uniqueId);
  return {
    provinceCode: id.substring(0, 1),
    districtCode: id.substring(0, 2),
    dsdCode:      id.substring(2, 4),
  };
}

/**
 * Check whether a user may access a specific record location.
 * provinceCode / districtCode / dsdCode are all plain codes (as stored in records).
 */
export function canAccessLocation(userPermissions, { provinceCode, districtCode, dsdCode }) {
  if (!userPermissions || userPermissions.status !== 'active') return false;
  const { access_level, allowed_provinces, allowed_districts, allowed_dsds } = userPermissions;

  if (!access_level || access_level === 'all-island') return true;

  if (access_level === 'province')
    return splitCodes(allowed_provinces).includes(String(provinceCode));

  if (access_level === 'district')
    return splitCodes(allowed_districts).includes(String(districtCode));

  if (access_level === 'dsd') {
    // allowed_dsds stores combined keys: "11_03,11_06"
    const combinedKey = `${districtCode}_${dsdCode}`;
    return splitCodes(allowed_dsds).includes(combinedKey);
  }

  return false;
}

/**
 * Filter a locationData object so only the user's allowed locations appear in dropdowns.
 *
 * Works with both formats used in this codebase:
 *   - Plain DSD codes  (getLocationData  → Register, Search)
 *   - Combined keys    (getLocationDataForAnalytics → UserManagement)
 */
export function filterLocationData(locationData, userPermissions) {
  if (!userPermissions || userPermissions.status !== 'active')
    return { provinces: [], districts: {}, divisions: {} };

  const { access_level, allowed_provinces, allowed_districts, allowed_dsds } = userPermissions;

  if (!access_level || access_level === 'all-island') return locationData;

  const extra = { sheetIds: locationData.sheetIds || {} };

  // ── Province-level ────────────────────────────────────────────────────────
  if (access_level === 'province') {
    const allowedProvs = splitCodes(allowed_provinces);
    const provinces = (locationData.provinces || []).filter(p =>
      allowedProvs.includes(String(p.code))
    );
    const districts = {};
    const divisions = {};
    provinces.forEach(p => {
      const pc = String(p.code);
      const distList = locationData.districts?.[pc] || [];
      districts[pc] = distList;
      distList.forEach(d => {
        const dc = String(d.code);
        if (locationData.divisions?.[dc]) divisions[dc] = locationData.divisions[dc];
      });
    });
    return { provinces, districts, divisions, ...extra };
  }

  // ── District-level ────────────────────────────────────────────────────────
  if (access_level === 'district') {
    const allowedDists = splitCodes(allowed_districts);
    const provinces  = [];
    const districts  = {};
    const divisions  = {};
    Object.entries(locationData.districts || {}).forEach(([pc, distList]) => {
      const filtered = distList.filter(d => allowedDists.includes(String(d.code)));
      if (!filtered.length) return;
      districts[pc] = filtered;
      const prov = (locationData.provinces || []).find(p => String(p.code) === pc);
      if (prov && !provinces.find(p => p.code === prov.code)) provinces.push(prov);
      filtered.forEach(d => {
        const dc = String(d.code);
        if (locationData.divisions?.[dc]) divisions[dc] = locationData.divisions[dc];
      });
    });
    return { provinces, districts, divisions, ...extra };
  }

  // ── DSD-level ─────────────────────────────────────────────────────────────
  if (access_level === 'dsd') {
    const allowedDSDs = splitCodes(allowed_dsds); // ["11_03", "11_06", ...]
    const allowedByDistrict = {};
    allowedDSDs.forEach(key => {
      const [dc, dsd] = key.split('_');
      if (!allowedByDistrict[dc]) allowedByDistrict[dc] = [];
      allowedByDistrict[dc].push(dsd);
    });
    const allowedDistCodes = Object.keys(allowedByDistrict);

    const provinces = [];
    const districts = {};
    const divisions = {};

    Object.entries(locationData.districts || {}).forEach(([pc, distList]) => {
      const filteredDists = distList.filter(d => allowedDistCodes.includes(String(d.code)));
      if (!filteredDists.length) return;
      districts[pc] = filteredDists;
      const prov = (locationData.provinces || []).find(p => String(p.code) === pc);
      if (prov && !provinces.find(p => p.code === prov.code)) provinces.push(prov);
      filteredDists.forEach(d => {
        const dc = String(d.code);
        const allowedPlain = allowedByDistrict[dc] || [];
        const divList = locationData.divisions?.[dc] || [];
        const filteredDivs = divList.filter(div => {
          const code = String(div.code);
          return code.includes('_')
            ? allowedDSDs.includes(code)
            : allowedPlain.includes(code);
        });
        if (filteredDivs.length) divisions[dc] = filteredDivs;
      });
    });
    return { provinces, districts, divisions, ...extra };
  }

  return locationData;
}

/**
 * Filter a list of platform users so an admin only sees users within their scope.
 * All-island admins see everyone. Province/district admins see users whose locations
 * fall within their allowed areas. Pending users (no location yet) are always included.
 */
export function filterUsersByAdminScope(users, adminPermissions) {
  if (!adminPermissions || adminPermissions.status !== 'active') return users;
  const { access_level, allowed_provinces, allowed_districts, allowed_dsds } = adminPermissions;
  if (!access_level || access_level === 'all-island') return users;

  const adminProvinces = splitCodes(allowed_provinces);
  const adminDistricts = splitCodes(allowed_districts);
  const adminDsds      = splitCodes(allowed_dsds);

  return users.filter(user => {
    const userLevel = (user.access_level || '').trim();

    // Pending / unassigned users: no location yet — always visible to any admin
    if (!userLevel) return true;

    if (access_level === 'province') {
      if (userLevel === 'all-island') return false;
      if (userLevel === 'province')
        return splitCodes(user.allowed_provinces).some(p => adminProvinces.includes(String(p)));
      if (userLevel === 'district')
        return splitCodes(user.allowed_districts).some(d => adminProvinces.includes(String(d).charAt(0)));
      if (userLevel === 'dsd')
        return splitCodes(user.allowed_dsds).some(dsd => {
          const dc = dsd.split('_')[0];
          return dc && adminProvinces.includes(String(dc).charAt(0));
        });
    }

    if (access_level === 'district') {
      if (userLevel === 'all-island' || userLevel === 'province') return false;
      if (userLevel === 'district')
        return splitCodes(user.allowed_districts).some(d => adminDistricts.includes(String(d)));
      if (userLevel === 'dsd')
        return splitCodes(user.allowed_dsds).some(dsd => {
          const dc = dsd.split('_')[0];
          return dc && adminDistricts.includes(String(dc));
        });
    }

    if (access_level === 'dsd') {
      if (userLevel !== 'dsd') return false;
      return splitCodes(user.allowed_dsds).some(dsd => adminDsds.includes(dsd));
    }

    return false;
  });
}

/**
 * Returns which analytics levels a user may view based on their access level.
 * e.g. a district-level user can only pick district or ds-division analytics.
 */
export function allowedAnalyticsLevels(userPermissions) {
  if (!userPermissions || userPermissions.status !== 'active') return [];
  const map = {
    'all-island': ['all-island', 'province', 'district', 'ds-division'],
    'province':   ['province', 'district', 'ds-division'],
    'district':   ['district', 'ds-division'],
    'dsd':        ['ds-division'],
  };
  return map[userPermissions.access_level] || ['all-island', 'province', 'district', 'ds-division'];
}
